import React, { useEffect, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { toast } from 'sonner';
import { MAX_DECIMAL_PLACES } from '../../helpers/constants';
import {
  evaluateArithmeticExpression,
  isArithmeticExpression
} from '../../helpers/evaluateArithmeticExpression';

const formatValue = (num, decimalPlaces = MAX_DECIMAL_PLACES) => {
  if (typeof num !== 'number') return '';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  });
};

const sanitizeExpressionInput = rawValue => rawValue.replace(/[^0-9.=+\-*/()\s]/g, '');

const sanitizeNumericInput = (rawValue, decimalPlaces) => {
  let sanitizedValue = rawValue.replace(/[^0-9.-]/g, '').trim();

  const decimalIndex = sanitizedValue.indexOf('.');
  if (decimalIndex !== -1) {
    const decimalPart = sanitizedValue.substring(decimalIndex + 1);
    if (decimalPart.length > decimalPlaces) {
      sanitizedValue = sanitizedValue.substring(0, decimalIndex + 1 + decimalPlaces);
    }
  }

  return sanitizedValue;
};

const getDisplayValue = (value, decimalPlaces) => {
  if (value === '' || value === null || value === undefined) return '';
  if (typeof value === 'number') return formatValue(value, decimalPlaces);
  return String(value);
};

const CurrencyInput = ({
  value,
  onChange,
  disabled = false,
  className = '',
  decimalPlaces = MAX_DECIMAL_PLACES,
  height = '45px',
  inputStyle,
  placeholder = '',
  allowExpressions = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [hasPendingExpression, setHasPendingExpression] = useState(false);

  useEffect(() => {
    if (hasPendingExpression) return;
    setInputValue(getDisplayValue(value, decimalPlaces));
  }, [value, decimalPlaces, hasPendingExpression]);

  const revertToCommittedValue = () => {
    setHasPendingExpression(false);
    setInputValue(getDisplayValue(value, decimalPlaces));
  };

  const commitNumericValue = sanitizedValue => {
    if (sanitizedValue === '-') {
      setInputValue(sanitizedValue);
      onChange('');
      return;
    }

    const numberValue = parseFloat(sanitizedValue);

    if (!isNaN(numberValue)) {
      if (sanitizedValue.match(/\.0+$/)) {
        setInputValue(sanitizedValue);
        onChange(numberValue);
      } else {
        let formatedValue = formatValue(numberValue, decimalPlaces);
        if (sanitizedValue.endsWith('.')) {
          formatedValue += '.';
        }
        setInputValue(formatedValue);
        onChange(numberValue);
      }
      return;
    }

    setInputValue('');
    onChange('');
  };

  const commitInputValue = rawValue => {
    const trimmedValue = String(rawValue ?? '').trim();

    if (!trimmedValue) {
      setHasPendingExpression(false);
      setInputValue('');
      onChange('');
      return;
    }

    if (allowExpressions && isArithmeticExpression(trimmedValue)) {
      const result = evaluateArithmeticExpression(trimmedValue, decimalPlaces);
      if (!result.ok) {
        toast.warning('Invalid expression');
        revertToCommittedValue();
        return;
      }

      setHasPendingExpression(false);
      setInputValue(formatValue(result.value, decimalPlaces));
      onChange(result.value);
      return;
    }

    setHasPendingExpression(false);
    commitNumericValue(sanitizeNumericInput(trimmedValue, decimalPlaces));
  };

  const handleInputChange = e => {
    const rawValue = e.target.value;

    if (allowExpressions && (isArithmeticExpression(rawValue) || hasPendingExpression)) {
      const sanitizedExpression = sanitizeExpressionInput(rawValue);
      setInputValue(sanitizedExpression);
      setHasPendingExpression(Boolean(sanitizedExpression.trim()));
      return;
    }

    setHasPendingExpression(false);
    commitNumericValue(sanitizeNumericInput(rawValue, decimalPlaces));
  };

  const handleFocus = () => {
    if (!allowExpressions || hasPendingExpression) return;
    if (typeof value === 'number' && Number.isFinite(value)) {
      setInputValue(String(value));
    }
  };

  const handleKeyDown = e => {
    if (!allowExpressions || e.key !== 'Enter') return;
    e.preventDefault();
    commitInputValue(inputValue);
    e.target.blur();
  };

  const handleBlur = () => {
    if (!allowExpressions) return;
    if (hasPendingExpression || isArithmeticExpression(inputValue)) {
      commitInputValue(inputValue);
    } else {
      setHasPendingExpression(false);
      setInputValue(getDisplayValue(value, decimalPlaces));
    }
  };

  return (
    <FormControl
      type="text"
      disabled={disabled}
      placeholder={placeholder}
      value={inputValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className={`text-start ${className}`}
      style={{ height, ...inputStyle }}
    />
  );
};

export default CurrencyInput;
